import { z } from 'zod';

export const AcademicBaseGradeSchema = z.enum(['A', 'B', 'C', 'D', 'F']);
export type AcademicBaseGrade = z.infer<typeof AcademicBaseGradeSchema>;

export const AcademicGradeModifierSchema = z.enum(['+', 'none', '-']);
export type AcademicGradeModifier = z.infer<typeof AcademicGradeModifierSchema>;

export const AcademicGradeEntryStudentStatusSchema = z.enum([
  'normal',
  'needs-attention',
  'below-passing',
]);
export type AcademicGradeEntryStudentStatus = z.infer<typeof AcademicGradeEntryStudentStatusSchema>;

export const AcademicGradebookWorkflowStateSchema = z.enum(['draft', 'submitted']);
export type AcademicGradebookWorkflowState = z.infer<typeof AcademicGradebookWorkflowStateSchema>;

export const AcademicGradeHistoryToneSchema = z.enum(['neutral', 'positive', 'warning']);
export type AcademicGradeHistoryTone = z.infer<typeof AcademicGradeHistoryToneSchema>;

export const AcademicGradeHistoryReferenceSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(['previous-period', 'prior-year', 'latest-note']),
  label: z.string().min(1),
  grade: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  tone: AcademicGradeHistoryToneSchema,
}).strict();
export type AcademicGradeHistoryReference = z.infer<typeof AcademicGradeHistoryReferenceSchema>;

export const AcademicGradeEntryStudentSchema = z.object({
  studentKey: z.string().min(1),
  studentId: z.string().nullable().optional(),
  name: z.string().min(1),
  baseGrade: AcademicBaseGradeSchema.nullable(),
  modifier: AcademicGradeModifierSchema.default('none'),
  grade: z.string().nullable(),
  comment: z.string().nullable().optional(),
  hasComment: z.boolean(),
  status: AcademicGradeEntryStudentStatusSchema,
  history: z.array(AcademicGradeHistoryReferenceSchema).default([]),
}).strict();
export type AcademicGradeEntryStudent = z.infer<typeof AcademicGradeEntryStudentSchema>;

export const AcademicGradeEntryProgressSchema = z.object({
  totalStudents: z.number().int().nonnegative(),
  gradedStudents: z.number().int().nonnegative(),
  ungradedStudents: z.number().int().nonnegative(),
  percentComplete: z.number().int().nonnegative(),
}).strict();
export type AcademicGradeEntryProgress = z.infer<typeof AcademicGradeEntryProgressSchema>;

export const AcademicGradeEntryWorkspaceSchema = z.object({
  gradebookId: z.string().min(1),
  sectionKey: z.string().min(1),
  markingPeriodKey: z.string().min(1),
  subjectTitle: z.string().min(1),
  classLabel: z.string().min(1),
  teacherName: z.string().min(1),
  academicYear: z.string().min(1),
  periodLabel: z.string().min(1),
  workflowState: AcademicGradebookWorkflowStateSchema,
  submittedAt: z.string().nullable().optional(),
  updatedAt: z.string().min(1),
  progress: AcademicGradeEntryProgressSchema,
  students: z.array(AcademicGradeEntryStudentSchema).default([]),
}).strict();
export type AcademicGradeEntryWorkspace = z.infer<typeof AcademicGradeEntryWorkspaceSchema>;

export const GetGradeEntryWorkspaceByRouteInputSchema = z.object({
  routeKey: z.string().min(1),
}).strict();
export type GetGradeEntryWorkspaceByRouteInput = z.infer<typeof GetGradeEntryWorkspaceByRouteInputSchema>;

export const GetGradeEntryWorkspaceByContextInputSchema = z.object({
  sectionKey: z.string().min(1),
  markingPeriodKey: z.string().min(1),
}).strict();
export type GetGradeEntryWorkspaceByContextInput = z.infer<typeof GetGradeEntryWorkspaceByContextInputSchema>;

export const GetGradeEntryWorkspaceInputSchema = z.union([
  GetGradeEntryWorkspaceByRouteInputSchema,
  GetGradeEntryWorkspaceByContextInputSchema,
]);
export type GetGradeEntryWorkspaceInput = z.infer<typeof GetGradeEntryWorkspaceInputSchema>;

export const AcademicGradeEntryMutationEntrySchema = z.object({
  studentKey: z.string().min(1),
  baseGrade: AcademicBaseGradeSchema.nullable(),
  modifier: AcademicGradeModifierSchema.default('none'),
  comment: z.string().max(2000).nullable().optional(),
}).strict();
export type AcademicGradeEntryMutationEntry = z.infer<typeof AcademicGradeEntryMutationEntrySchema>;

export const SaveGradeEntryDraftInputSchema = z.object({
  gradebookId: z.string().min(1),
  entries: z.array(AcademicGradeEntryMutationEntrySchema).min(1),
}).strict();
export type SaveGradeEntryDraftInput = z.infer<typeof SaveGradeEntryDraftInputSchema>;

export const SubmitGradeEntryInputSchema = z.object({
  gradebookId: z.string().min(1),
  entries: z.array(AcademicGradeEntryMutationEntrySchema).min(1),
}).strict();
export type SubmitGradeEntryInput = z.infer<typeof SubmitGradeEntryInputSchema>;

export const formatAcademicCompositeGrade = (
  baseGrade: AcademicBaseGrade | null | undefined,
  modifier: AcademicGradeModifier | null | undefined,
): string | null => {
  if (!baseGrade) {
    return null;
  }
  if (baseGrade === 'F') {
    return 'F';
  }
  if (!modifier || modifier === 'none') {
    return baseGrade;
  }
  return `${baseGrade}${modifier}`;
};

export const deriveAcademicGradeEntryStudentStatus = (input: {
  baseGrade: AcademicBaseGrade | null | undefined;
}): AcademicGradeEntryStudentStatus => {
  if (!input.baseGrade) {
    return 'needs-attention';
  }
  if (input.baseGrade === 'C' || input.baseGrade === 'D' || input.baseGrade === 'F') {
    return 'below-passing';
  }
  return 'normal';
};

export const buildAcademicGradeEntryProgress = (
  students: readonly Pick<AcademicGradeEntryStudent, 'baseGrade'>[],
): AcademicGradeEntryProgress => {
  const totalStudents = students.length;
  const gradedStudents = students.filter((student) => Boolean(student.baseGrade)).length;
  const ungradedStudents = Math.max(0, totalStudents - gradedStudents);
  const percentComplete = totalStudents > 0
    ? Math.round((gradedStudents / totalStudents) * 100)
    : 0;

  return {
    totalStudents,
    gradedStudents,
    ungradedStudents,
    percentComplete,
  };
};
